import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useAlert } from '@lwtears/lwt-common-frontend/lib/Alert';
import CloseButton from '../common/Buttons/CloseButton';
import NavigationMenu from './NavigationMenu';
import { remCalc } from '../../util/style-util';

const SectionHeader: React.FC<{
  backRoute?: string;
  children?: React.ReactNode;
  closeButtonAction?: any;
  hamburgerMenu?: boolean;
  showCloseButton?: boolean;
  showDashboardIcon?: boolean;
  title?: string;
  omitRoutes?: string[];
}> = ({
  backRoute,
  children,
  closeButtonAction,
  hamburgerMenu = true,
  showCloseButton,
  showDashboardIcon = true,
  title,
  omitRoutes,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [, { closeAlert }] = useAlert();

  const toggle = () => setOpen(!open);

  const navigate = useNavigate();
  const location = useLocation();

  const isDashboard = location?.pathname === '/home';
  const backNavigationRoute = backRoute || '/home';

  const backNavigationAction = () => {
    closeAlert();
    navigate(backNavigationRoute);
  };

  const icon = backNavigationRoute === '/home' ? faHome : faArrowLeft;

  return (
    <div className={classes.sectionHeaderRoot}>
      <AppBar id="section-header" position="static" className={classes.sectionHeaderAppBarRoot}>
        <Toolbar classes={{ root: classes.sectionHeaderCustomToolbar }}>
          <div className={classes.leftIcons}>
            {hamburgerMenu && (
              <>
                <IconButton
                  onClick={toggle}
                  edge="start"
                  className={classes.menuButton}
                  color="inherit"
                  aria-label={tA11y('buttons.menu')}
                >
                  {open ? <CloseIcon /> : <MenuIcon />}
                  <NavigationMenu open={open} toggle={toggle} omitRoutes={omitRoutes} />
                </IconButton>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            {!isDashboard && showDashboardIcon && (
              <>
                <IconButton
                  onClick={backNavigationAction}
                  edge="start"
                  className={classes.homeButton}
                  color="inherit"
                  aria-label={tA11y('buttons.menu')}
                >
                  <FontAwesomeIcon icon={icon} />
                </IconButton>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            <Typography variant="h4" className={classes.title}>
              {title ?? ''}
            </Typography>
          </div>
          <div className={classes.rightIcons}>
            {children}
            {showCloseButton && (
              <>
                <Divider orientation="vertical" />
                <CloseButton onClick={closeButtonAction} color={'white'} margin={'0 -12px 0 0'} />
              </>
            )}
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  sectionHeaderRoot: {
    top: 0,
    flexGrow: 1,
    position: 'sticky',
    zIndex: 99,
  },
  sectionHeaderCustomToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: remCalc(theme.constants.app.menuOffset),
  },
  leftIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    height: remCalc(theme.constants.app.menuOffset),
  },
  rightIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    height: remCalc(theme.constants.app.menuOffset),
  },
  sectionHeaderAppBarRoot: {
    backgroundColor: `${theme.palette.secondary.dark} !important`,
    color: 'white !important',
  },
  menuButton: {
    paddingRight: 20,
    paddingLeft: 20,
  },
  homeButton: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  title: {
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      fontSize: '1.4rem',
    },
  },
  closeButtonDiv: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    margin: theme.spacing(3),
  },
  closeButton: {
    paddingLeft: 15,
    [theme.breakpoints.down('md')]: {
      paddingLeft: 20,
    },
  },
}));

export default SectionHeader;
